import { useState, useEffect } from "react";
import dayjs from "dayjs";

import { Text, Button, ErrorMsg } from "@fragments";

import * as Material from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { SlowMotionVideoRounded } from "@mui/icons-material";
import { ShutterSpeedRounded } from "@material-ui/icons";

const DURATION_PRESETS = [7, 15, 21, 30, 60];
const DEFAULT_DURATION = 21;
const MIN_DURATION = 7;
const MAX_DURATION = 120;
const DATE_FORMAT = "YYYY-MM-DD";

function safeDayjs(value) {
    const d = dayjs(value);
    return d.isValid() ? d : dayjs();
}

function getInitialDuration(interval) {
    if (!interval?.start_date || !interval?.end_date) return DEFAULT_DURATION;
    const start = dayjs(interval.start_date);
    const end = dayjs(interval.end_date);
    if (!start.isValid() || !end.isValid()) return DEFAULT_DURATION;
    const diff = end.diff(start, "day");
    return diff > 0 ? diff : DEFAULT_DURATION;
}

export default function ExpirationSettings({
    configRef,
    errors = {},
    clearError = () => {},
}) {
    if (!configRef.current.interval || typeof configRef.current.interval !== "object") {
        configRef.current.interval = { start_date: null, end_date: null };
    }

    const [duration, setDuration] = useState(() =>
        getInitialDuration(configRef.current.interval)
    );
    const [startAt, setStartAt] = useState(() =>
        safeDayjs(configRef.current.interval.start_date)
    );

    const today = dayjs().startOf("day");

    useEffect(() => {
        configRef.current.interval = {
            start_date: startAt.format(DATE_FORMAT),
            end_date: startAt.add(duration, "day").format(DATE_FORMAT),
        };
    }, [startAt, duration]);

    function handleStartChange(v) {
        const next = safeDayjs(v);
        setStartAt(next);
        if (errors?.startDate) clearError("startDate");
        if (errors?.endDate) clearError("endDate");
    }

    return (
        <>
            <Text.h3
                className="mt-4"
                value="Duração do desafio"
                grow
                left={<ShutterSpeedRounded />}
            />

            <Material.Stack
                spacing={1}
                direction="row"
                alignItems="center"
                className="px-2 mb-1 mt-2"
            >
                <Material.Slider
                    color="primary"
                    aria-label="Duração"
                    value={duration}
                    onChange={(e) => {
                        setDuration(e.target.value);
                        if (errors?.endDate) clearError("endDate");
                    }}
                    min={MIN_DURATION}
                    max={MAX_DURATION}
                />
                <Material.Typography
                    variant="h5"
                    style={{ textWrap: "nowrap" }}
                >
                    {duration} dias
                </Material.Typography>
            </Material.Stack>

            <div className="durations-row">
                {DURATION_PRESETS.map((preset) => (
                    <Button
                        key={preset}
                        size="small"
                        onClick={() => {
                            setDuration(preset);
                            if (errors?.endDate) clearError("endDate");
                        }}
                        text={`${preset} dias`}
                        color={duration === preset ? "primary" : "overlay"}
                    />
                ))}
            </div>

            {errors?.endDate && <ErrorMsg>{errors.endDate}</ErrorMsg>}

            <Text.h3
                className="mt-5"
                value="Data de início"
                grow
                left={<SlowMotionVideoRounded />}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                    size="small"
                    value={startAt}
                    onChange={handleStartChange}
                    minDate={today}
                    disablePast
                    id="calendar"
                    color="primary"
                    showDaysOutsideCurrentMonth
                    className="challenge-calendar"
                />
            </LocalizationProvider>

            {errors?.startDate && <ErrorMsg>{errors.startDate}</ErrorMsg>}
        </>
    );
}
